import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";

// Pointage — clicoo AttendanceController (/api/attendance/*).

export const VERIFY_MODE_LABELS = {
    fingerprint: "Empreinte",
    face: "Visage",
    card: "Badge",
    password: "Code",
    manual: "Manuel",
    other: "Autre",
};

export const SOURCE_LABELS = {
    push: "Pointeuse",
    pos_import: "Import caisse",
    manual: "Saisie manuelle",
};

const headers = async () => {
    const account = await secureStorage.getItem("selectedAccount");
    return {"X-account-id": account?.id, Accept: "application/json"};
};

// Error message sent by the API ({error}), or a generic fallback.
export const apiErrorMessage = (error, fallback) =>
    error?.response?.data?.error || error?.response?.data?.detail || fallback;

const periodParams = ({from, to, dayStart, employeeId}) => {
    const params = new URLSearchParams({from, to});
    if (dayStart) params.set("day_start", dayStart);
    if (employeeId) params.set("employee_id", employeeId);
    return params.toString();
};

// Module on/off + work-day start, per account.
export const fetchAttendanceSettings = async () => {
    const {data} = await api.get("/attendance/settings", {headers: await headers()});
    return data;
};

export const updateAttendanceSettings = async (changes) => {
    const {data} = await api.put("/attendance/settings", changes, {headers: await headers()});
    return data;
};

export const fetchAttendanceSummary = async (period) => {
    const {data} = await api.get(`/attendance/summary?${periodParams(period)}`, {headers: await headers()});
    return data;
};

export const fetchAttendancePunches = async (period) => {
    const {data} = await api.get(`/attendance/punches?${periodParams(period)}`, {headers: await headers()});
    return data;
};

export const createManualPunch = async ({employeeId, punchedAt, note}) => {
    const {data} = await api.post(
        "/attendance/punches",
        {employee_id: employeeId, punched_at: punchedAt, note},
        {headers: await headers()}
    );
    return data;
};

export const deletePunch = async (id) => {
    await api.delete(`/attendance/punches/${id}`, {headers: await headers()});
};

export const fetchAttendanceDevices = async () => {
    const {data} = await api.get("/attendance/devices", {headers: await headers()});
    return data;
};

export const createAttendanceDevice = async (name) => {
    const {data} = await api.post("/attendance/devices", {name}, {headers: await headers()});
    return data;
};

export const updateAttendanceDevice = async ({id, ...changes}) => {
    const {data} = await api.put(`/attendance/devices/${id}`, changes, {headers: await headers()});
    return data;
};

export const regenerateDeviceToken = async (id) => {
    const {data} = await api.post(`/attendance/devices/${id}/regenerate-token`, {}, {headers: await headers()});
    return data;
};

export const deleteAttendanceDevice = async (id) => {
    await api.delete(`/attendance/devices/${id}`, {headers: await headers()});
};

// ── Dates (local, YYYY-MM-DD) ───────────────────────────────────────────────

export const toDateKey = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const presetRange = (preset) => {
    const today = new Date();
    const d = (offset) => {
        const x = new Date(today);
        x.setDate(x.getDate() + offset);
        return x;
    };
    switch (preset) {
        case "yesterday":
            return {from: toDateKey(d(-1)), to: toDateKey(d(-1))};
        case "week": {
            const mondayOffset = -((today.getDay() + 6) % 7);
            return {from: toDateKey(d(mondayOffset)), to: toDateKey(today)};
        }
        case "month":
            return {from: toDateKey(new Date(today.getFullYear(), today.getMonth(), 1)), to: toDateKey(today)};
        case "lastMonth":
            return {
                from: toDateKey(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
                to: toDateKey(new Date(today.getFullYear(), today.getMonth(), 0)),
            };
        default:
            return {from: toDateKey(today), to: toDateKey(today)};
    }
};

// The period before (-1) or after (+1): a day stays a day, a month or a Monday-based
// week moves by whole months / weeks, anything else by its own length. Never past today.
export const shiftPeriod = ({from, to}, direction, today = toDateKey(new Date())) => {
    const start = new Date(`${from}T12:00:00`);
    const end = new Date(`${to}T12:00:00`);
    const length = Math.round((end - start) / 86400000) + 1;
    let next;
    if (length > 1 && start.getDate() === 1 && start.getMonth() === end.getMonth()) {
        next = {
            from: toDateKey(new Date(start.getFullYear(), start.getMonth() + direction, 1)),
            to: toDateKey(new Date(start.getFullYear(), start.getMonth() + direction + 1, 0)),
        };
    } else {
        const isWeek = length > 1 && length <= 7 && start.getDay() === 1;
        const step = isWeek ? 7 : length;
        const moved = new Date(start);
        moved.setDate(moved.getDate() + direction * step);
        const movedEnd = new Date(moved);
        movedEnd.setDate(movedEnd.getDate() + (isWeek ? 6 : length - 1));
        next = {from: toDateKey(moved), to: toDateKey(movedEnd)};
    }
    return {from: next.from, to: next.to > today ? today : next.to};
};

export const formatDuration = (minutes) => {
    if (!minutes) return "0h00";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h${String(m).padStart(2, "0")}`;
};

export const formatDayLabel = (dateKey) =>
    new Date(`${dateKey}T12:00:00`).toLocaleDateString("fr-FR", {weekday: "short", day: "2-digit", month: "2-digit"});
